import { GraduationCap, Users } from 'lucide-react';

import { SkeletonLines } from '@/components/ui/Skeleton';
import { formatNumber } from '@/lib/format';
import type { PlatformStats } from '@/types/institute';
import { CountUp } from './CountUp';

interface ReachPanelProps {
  stats: PlatformStats | null;
  isLoading: boolean;
}

export function ReachPanel({ stats, isLoading }: ReachPanelProps) {
  const students = stats?.totalStudents ?? 0;
  const staff = stats?.totalStaff ?? 0;
  // Live children over live institutes: a school still being set up, or
  // suspended, has no roll worth averaging in.
  const liveInstitutes = stats?.activeInstitutes ?? 0;
  const liveStudents = stats?.studentsAtLiveInstitutes ?? 0;

  const averageRoll = liveInstitutes > 0 ? Math.round(liveStudents / liveInstitutes) : null;
  const childrenPerStaff = staff > 0 ? Math.round(students / staff) : null;

  return (
    <section className="flex flex-col justify-between rounded-2xl border border-slate-200 dark:border-slate-700/80 bg-white dark:bg-slate-900 p-4 sm:p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div>
        <h2 className="text-[12.5px] font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
          People on the platform
        </h2>

        {isLoading ? (
          <SkeletonLines count={4} />
        ) : (
          <>
            <div className="mt-3 grid grid-cols-2 gap-3">
              {/* Box 1: Children */}
              <div className="flex flex-col justify-between rounded-2xl border border-blue-100 dark:border-blue-900/50 bg-gradient-to-b from-white to-blue-50/70 dark:from-slate-900 dark:to-blue-950/20 p-3.5 shadow-[0_3px_12px_rgba(59,130,246,0.06)] transition-all hover:shadow-md">
                <div className="flex items-center justify-between">
                  <p className="text-[26px] font-black text-slate-900 dark:text-white leading-none">
                    <CountUp value={students} />
                  </p>
                  <span className="grid h-7 w-7 place-items-center rounded-lg bg-blue-100/80 text-blue-700 dark:bg-blue-950/60 dark:text-blue-400 border border-blue-200/60 dark:border-blue-800 shadow-2xs">
                    <GraduationCap className="h-4 w-4" />
                  </span>
                </div>
                <p className="mt-2 text-[11.5px] font-semibold text-slate-600 dark:text-slate-400">
                  Students enrolled
                </p>
              </div>

              {/* Box 2: Staff */}
              <div className="flex flex-col justify-between rounded-2xl border border-purple-100 dark:border-purple-900/50 bg-gradient-to-b from-white to-purple-50/70 dark:from-slate-900 dark:to-purple-950/20 p-3.5 shadow-[0_3px_12px_rgba(147,51,234,0.06)] transition-all hover:shadow-md">
                <div className="flex items-center justify-between">
                  <p className="text-[26px] font-black text-slate-900 dark:text-white leading-none">
                    <CountUp value={staff} />
                  </p>
                  <span className="grid h-7 w-7 place-items-center rounded-lg bg-purple-100/80 text-purple-700 dark:bg-purple-950/60 dark:text-purple-400 border border-purple-200/60 dark:border-purple-800 shadow-2xs">
                    <Users className="h-4 w-4" />
                  </span>
                </div>
                <p className="mt-2 text-[11.5px] font-semibold text-slate-600 dark:text-slate-400">
                  Staff accounts
                </p>
              </div>
            </div>

            <div className="mt-3.5 space-y-2 border-t border-slate-100 dark:border-slate-800 pt-3 text-[12px]">
              <div className="flex items-center justify-between text-slate-600 dark:text-slate-400 font-medium">
                <span>Average enrolled per live institute</span>
                <span className="rounded-md bg-slate-100 dark:bg-slate-800 px-2 py-0.5 font-bold text-slate-800 dark:text-slate-200 tabular-nums">
                  {averageRoll === null ? '—' : formatNumber(averageRoll)}
                </span>
              </div>
              <div className="flex items-center justify-between text-slate-600 dark:text-slate-400 font-medium">
                <span>Students per staff account</span>
                <span className="rounded-md bg-slate-100 dark:bg-slate-800 px-2 py-0.5 font-bold text-slate-800 dark:text-slate-200 tabular-nums">
                  {childrenPerStaff === null ? '—' : formatNumber(childrenPerStaff)}
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
